import { createContext, ReactNode, useContext, useEffect, useState, useCallback } from "react";
import isEqual from "lodash.isequal";

import { FeatureFlags, resolveFeatureFlags } from "./FeatureFlagResolver";
import { useAllDivisionData } from "../../reactQuery/division/divisionsQuery";



type AllDivisionFlags = Record<string, FeatureFlags>;
type RuntimeOverrides = Record<string, FeatureFlags>;

interface FeatureFlagContextType {
       flags: AllDivisionFlags;
       overrideFeature: (divisionCode: string, key: string, value: boolean) => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
       flags: {},
       overrideFeature: () => { },
});

interface FeatureFlagProviderProps {
       children: ReactNode;
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
       const [allFlags, setAllFlags] = useState<AllDivisionFlags>({});
       const [runtimeOverrides, setRuntimeOverrides] = useState<RuntimeOverrides>({});

       const { data: divisions, isSuccess } = useAllDivisionData();

       useEffect(() => {
              if (!isSuccess || !divisions) return;

              const resolved: AllDivisionFlags = {};

              divisions.data.forEach((division) => {
                     if (division.divisionCode) {
                            resolved[division.divisionCode] = resolveFeatureFlags([{ code: division.divisionCode, features: division.features }], division.divisionCode);
                     }
              });

              const merged: AllDivisionFlags = { ...resolved };
              for (const divCode in runtimeOverrides) {
                     merged[divCode] = {
                            ...merged[divCode],
                            ...runtimeOverrides[divCode],
                     };
              }

              setAllFlags((prev) => (isEqual(prev, merged) ? prev : merged));
       }, [divisions, isSuccess, runtimeOverrides]);

       const overrideFeature = useCallback((divisionCode: string, key: string, value: boolean) => {
              setRuntimeOverrides((prev) => {
                     const current = prev[divisionCode] || {};
                     return {
                            ...prev,
                            [divisionCode]: {
                                   ...current,
                                   [key]: value,
                            },
                     };
              });
       }, []);

       return (
              <FeatureFlagContext.Provider value={{ flags: allFlags, overrideFeature }}>
                     {children}
              </FeatureFlagContext.Provider>
       );
};

export const useDivisionFeatureFlags = (divisionCode: string): FeatureFlags => {
       const { flags } = useContext(FeatureFlagContext);
       return flags[divisionCode] || {};
};

export const useDivisionFeatureFlag = (divisionCode: string, key: string): boolean => {
       const divisionFlags = useDivisionFeatureFlags(divisionCode);
       return !!divisionFlags[key];
};

export const useOverrideFeatureFlag = () => {
       const { overrideFeature } = useContext(FeatureFlagContext);
       return overrideFeature;
};
